/**
 * Клиент Admin API (ручки BLOCK 3 и вход AMEND-8).
 *
 * Имена полей наружу — те же, что в БД и в выгрузке SEON
 * (`reference/FIELD-MAP.md`, `backend/models/scoring.py`). Второго словаря имён
 * на фронте не заводим: расхождение «в базе `action_details`, на фронте
 * `score`» пришлось бы потом искать глазами при следующей выгрузке.
 *
 * Сессия ездит **только** кукой `httpOnly` (`services/admin_api/auth.py`):
 * токена в `localStorage` здесь нет и быть не может — код страницы куку не
 * видит, и XSS её не унесёт. Отсюда же следует, что у клиента нет состояния
 * «вошли»: единственный способ это узнать — спросить `/api/auth/me`.
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? ""

/** Пагинация вкладок — 100 строк, как на стороне ручки (`PAGE_SIZE`). */
export const PAGE_SIZE = 100

export const PERIODS = ["1D", "7D", "30D", "3M", "12M"] as const
export type Period = (typeof PERIODS)[number]

/**
 * Две вкладки выгрузки SEON плюс `local` — наши собственные правила, которых у
 * SEON нет вовсе (AMEND-13 §3: правило чёрного списка). Отдельный тип, а не
 * `custom`: `custom` перезаписывается следующей выгрузкой целиком.
 */
export type RuleType = "default" | "custom" | "local"

/**
 * Типы, у которых есть формат выгрузки SEON, — только ими оперируют импорт и
 * экспорт. `local` сюда не входит: отдать своё правило в выгрузке значило бы
 * выдать его за правило SEON.
 */
export type ExportedRuleType = "default" | "custom"
export type SortOrder = "asc" | "desc"

export interface Rule {
  id: number
  rule_type: string
  active: boolean
  seon_id: string
  name: string | null
  action: string | null
  action_details: string | null
  category: string | null
  created: string | null
  last_updated: string | null
  p95_runtime: string | null
  p99_runtime: string | null
  perf_check: string | null
}

interface PageMeta {
  total: number
  page: number
  page_size: number
  pages: number
}

export interface RulesPage extends PageMeta {
  items: Rule[]
}

export interface RuleStat {
  /** `scoring_rules.id` из каталога; срабатывание правила, которого в каталоге нет, — `null`. */
  rule_id: number | null
  seon_id: string
  rule_name: string | null
  rule_type: string
  action: string | null
  period: string
  times: number | null
  approved_pct: string | number | null
  reviewed_pct: string | number | null
  declined_pct: string | number | null
  last_trigger_date: string | null
}

export interface StatsPage extends PageMeta {
  items: RuleStat[]
}

export type Outcome = "approve" | "review" | "decline"

export interface ConfusionCell {
  period: string
  currency: string
  actual: string
  predicted: string
  count: number | null
  amount: string | number | null
  label: string | null
}

export interface ConfusionMatrix {
  period: string | null
  currency: string | null
  cells: ConfusionCell[]
  total_count: number
  total_amount: string | number | null
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/** Кого звать, когда API ответил 401. Ставит `AuthProvider`, см. ниже. */
type SessionLostHandler = () => void

let sessionLost: SessionLostHandler | null = null

/**
 * Регистрирует обработчик «сессия кончилась» и возвращает снятие подписки.
 *
 * Обработчик один, а не список: смысл у события ровно один — увести на экран
 * входа, — и второй подписчик означал бы два места, решающих это по-разному.
 *
 * Зачем это вообще: 401 прилетает не в момент клика по «войти», а посреди
 * работы — сессия истекла в базе или админ выключил человека, пока тот смотрел
 * в таблицу. Без этого крюка каждый экран показал бы своё «Request failed:
 * нужен вход», то есть ровно тот пустой экран, который бриф запрещает.
 */
export function onSessionLost(handler: SessionLostHandler): () => void {
  sessionLost = handler
  return () => {
    if (sessionLost === handler) sessionLost = null
  }
}

interface RequestOptions {
  /**
   * 401 здесь — нормальный ответ ручки, а не потеря сессии.
   *
   * Стоит у четырёх ручек входа: `login` так отвечает на неверный пароль,
   * `verify` — на неверный код, `me` — на «мы ещё не входили». Без этого флага
   * первая же опечатка в пароле читалась бы как «вас разлогинило» и уводила бы
   * человека на тот же экран, на котором он стоит, стирая набранное.
   */
  expected401?: boolean
}

async function request<T>(
  path: string,
  init?: RequestInit,
  options: RequestOptions = {},
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    headers: { Accept: "application/json" },
    // Кука сессии — единственный носитель входа, и без неё любой запрос
    // получит 401. `same-origin` сегодня и так значение по умолчанию, но
    // проставлено явно: молчаливый дефолт слишком легко считать «здесь про
    // куки не думали».
    credentials: "same-origin",
    ...init,
  })
  if (!response.ok) {
    // Текст ошибки FastAPI лежит в `detail`; если тело не разобралось —
    // показываем код, а не «Unexpected error».
    let detail = `HTTP ${response.status}`
    try {
      const body = (await response.json()) as { detail?: unknown }
      if (typeof body.detail === "string") detail = body.detail
    } catch {
      /* тело не JSON — остаётся код */
    }
    if (response.status === 401 && !options.expected401) sessionLost?.()
    throw new ApiError(response.status, detail)
  }
  return (await response.json()) as T
}

/**
 * Запрос, у которого в ответе тела нет, — 204 удаления.
 *
 * Отдельно от `request`, а не флагом внутри него: тот обязан вернуть `T`, и
 * «иногда `T`, иногда ничего» пришлось бы разбирать на каждом вызове. Разбор
 * ошибки повторён нарочно и целиком: разойдись он с `request`, 401 на удалении
 * перестал бы уводить на экран входа, и это не увидел бы никто.
 */
async function requestEmpty(path: string, init?: RequestInit): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    ...init,
  })
  if (response.ok) return

  let detail = `HTTP ${response.status}`
  try {
    const body = (await response.json()) as { detail?: unknown }
    if (typeof body.detail === "string") detail = body.detail
  } catch {
    /* тело не JSON — остаётся код */
  }
  if (response.status === 401) sessionLost?.()
  throw new ApiError(response.status, detail)
}

/** Заголовки запроса с телом. Отдельной функцией, чтобы не разъезжались. */
const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" }

/**
 * Собирает query-строку, выбрасывая пустые значения.
 *
 * Важно, что ключ запроса на клиенте строится из **того же** разобранного
 * набора параметров, что уходит в URL: если часть значений «дефолт по
 * умолчанию на сервере» и в URL не попадает, то один и тот же экран получает
 * два разных ключа кэша и лишний запрос. Поэтому все дефолты (`page`, `sort`,
 * `order`, `page_size`) проставляются здесь явно, а не молча на бэкенде.
 */
function query(params: Record<string, string | number | boolean | undefined | null>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ""
}

export interface RulesQuery {
  type: RuleType
  q?: string
  category?: string
  action?: string
  sort?: string
  order?: SortOrder
  page?: number
  page_size?: number
}

export function listRules(params: RulesQuery, signal?: AbortSignal) {
  const resolved = {
    type: params.type,
    q: params.q,
    category: params.category,
    action: params.action,
    sort: params.sort ?? "seon_id",
    order: params.order ?? "asc",
    page: params.page ?? 1,
    page_size: params.page_size ?? PAGE_SIZE,
  }
  return request<RulesPage>(`/api/scoring/rules${query(resolved)}`, { signal })
}

/** Подкабинет входа (AMEND-121): его аккаунты данных и аккаунт списков. */
export interface Cabinet {
  id: number | null
  name: string
  accounts: number[]
  lists_account_id: number
}

export interface Cabinets {
  items: Cabinet[]
  /** `null` — кука негодна (подкабинет не этого входа): выбрать заново. */
  selected: number | null
}

export function getCabinets(signal?: AbortSignal) {
  return request<Cabinets>("/api/cabinets", { signal })
}

/** Выбор запоминает сервер (кука) и сам же проверяет, что подкабинет — свой. */
export function selectCabinet(id: number) {
  return request<Cabinets>("/api/cabinets/select", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ id }),
  })
}

/** Тумблер ON/OFF. Тело — ровно `{active}`: ручка не принимает ничего другого. */
export function setRuleActive(id: number, active: boolean) {
  return request<Rule>(`/api/scoring/rules/${id}`, {
    method: "PATCH",
    headers: JSON_HEADERS,
    body: JSON.stringify({ active }),
  })
}

export interface StatsQuery {
  period?: Period
  type?: RuleType
  action?: string
  sort?: string
  order?: SortOrder
  page?: number
  page_size?: number
}

export function listStats(params: StatsQuery = {}, signal?: AbortSignal) {
  const resolved = {
    period: params.period,
    type: params.type,
    action: params.action,
    sort: params.sort ?? "times",
    order: params.order ?? "desc",
    page: params.page ?? 1,
    page_size: params.page_size ?? PAGE_SIZE,
  }
  return request<StatsPage>(`/api/scoring/stats${query(resolved)}`, { signal })
}

export function getConfusionMatrix(
  params: { period?: Period; currency?: string } = {},
  signal?: AbortSignal,
) {
  return request<ConfusionMatrix>(
    `/api/scoring/confusion-matrix${query(params)}`,
    { signal },
  )
}

// ── дашборд (AMEND-78) ───────────────────────────────────────────────────────

/** Число решений и их сумма в EUR. Сумма — строка-десятичная, как `Decimal` в остальных ручках. */
export interface OutcomeCell {
  count: number
  amount_eur: string
}

export type OutcomeCells = Record<Outcome, OutcomeCell>

export interface TransactionsPoint extends OutcomeCells {
  /** Шаг: `YYYY-MM-DD` (сутки, неделя с понедельника) или `YYYY-MM-DDTHH:00:00Z` (час). */
  t: string
}

export interface DashboardTransactions {
  period: Period
  from: string
  to: string
  bucket: "hour" | "day" | "week"
  series: TransactionsPoint[]
  totals: {
    count: number
    amount_eur: string
    saved_eur: string
    by_outcome: OutcomeCells
  }
  /** Решения без суммы или без курса ЕЦБ: в числе есть, в EUR — нет. */
  unconverted: { count: number; currencies: string[] }
}

export interface LocationRow {
  /** ISO 3166-1 alpha-2. */
  country: string
  approve: number
  review: number
  decline: number
  total: number
}

export interface DashboardLocations {
  period: Period
  items: LocationRow[]
  /** Решения без страны: сумма по странам плюс это число — итог Transactions. */
  no_country: number
}

export interface ScoreBin {
  score: number
  count: number
}

export interface DashboardScores {
  period: Period
  bins: ScoreBin[]
  thresholds: Pick<Thresholds, "approve_below" | "decline_at" | "source" | "is_assumption">
}

export function getDashboardTransactions(period: Period, signal?: AbortSignal) {
  return request<DashboardTransactions>(`/api/dashboard/transactions${query({ period })}`, { signal })
}

export function getDashboardLocations(period: Period, signal?: AbortSignal) {
  return request<DashboardLocations>(`/api/dashboard/locations${query({ period })}`, { signal })
}

export function getDashboardScores(period: Period, signal?: AbortSignal) {
  return request<DashboardScores>(`/api/dashboard/score-distribution${query({ period })}`, { signal })
}

export function getThresholds(signal?: AbortSignal) {
  return request<Thresholds>("/api/scoring/thresholds", { signal })
}

/** Адрес выгрузки. Скачиванием занимается браузер — это обычная ссылка. */
export function exportUrl(type: ExportedRuleType) {
  return `${BASE}/api/scoring/export?type=${encodeURIComponent(type)}`
}

// ── условие правила ──────────────────────────────────────────────────────────

/**
 * Узел условия — тот же, что в `backend/scoring/conditions.py`.
 *
 * Дерево показывается как есть. Пересказов вроде «почта подозрительная» здесь
 * нет: аналитик должен видеть, по чему именно правило срабатывает, иначе
 * карточка не годится для проверки.
 */
export interface ConditionNode {
  op: string
  field?: string | null
  value?: unknown
  args?: ConditionNode[] | null
}

/** Почему правило не считается — словами движка, не нашими. */
export interface RuleSkip {
  status: string
  reason: string | null
}

export interface RuleCondition {
  rule_id: number
  rule_type: string
  seon_id: string
  name: string | null
  active: boolean
  score: string | null
  score_error: string | null
  /** `null` — записи об условии нет вовсе; `unknown` — запись есть, но условие не читается. */
  status: string | null
  source: string | null
  version: number | null
  note: string | null
  missing: string[]
  condition: ConditionNode | null
  fields: string[]
  created_at: string | null
  /** Заполнен, если правило пропускается целиком. `null` — правило считается. */
  skip: RuleSkip | null
}

export function getRuleCondition(id: number, signal?: AbortSignal) {
  return request<RuleCondition>(`/api/scoring/rules/${id}/condition`, { signal })
}

// ── транзакции ───────────────────────────────────────────────────────────────

/** Префикс `id` строк двойника — `backend/seon_request.py::TWIN_ID_PREFIX`. */
export const TWIN_ID_PREFIX = "twin-"

/**
 * Строка списка `/transactions` — ровно `TransactionRow` из
 * `services/admin_api/transactions.py`: двенадцать колонок §4 AMEND-10 и
 * «SEON» (AMEND-60).
 *
 * `id` и `transaction_id` — **разные** колонки, и путать их дорого. `id` —
 * числовой идентификатор SEON: он в первичном ключе и в адресе карточки.
 * `transaction_id` — хэш, и показан в колонке `ID`. Приходит `id` строкой,
 * потому что в базе это текст, а не число.
 *
 * Денежное и `fraud_score` — `numeric` в Postgres, в JSON это **строка**: иначе
 * на длинных дробях поехала бы точность. Разбирают их `formatMoney` и
 * `formatPercent`, которым всё равно, что придёт.
 */
export interface Transaction {
  id: string
  transaction_id: string | null
  fraud_score: string | number | null
  /**
   * Временно, AMEND-60: балл SEON пары строки двойника. `null` — пары нет или
   * строка историческая (её `fraud_score` и есть балл SEON из выгрузки).
   */
  seon_fraud_score: string | number | null
  state: string | null
  user_id: string | null
  user_fullname: string | null
  transaction_amount_eur: string | number | null
  date: string | null
  label: string | null
  transaction_status: string | null
  assignee: string | null
  /** Теги одной строкой через запятую — так они лежат в выгрузке. */
  tags: string | null
}

/**
 * Страница списка. `has_more` — чтобы `Next` знал, гаснуть ли ему; от `total`
 * он не зависит.
 *
 * `total_capped` — подсчёт упёрся в потолок ручки (`COUNT_CAP`, AMEND-60 2.3):
 * строк **больше** `total`, и печатать его можно только с «+». Точное число —
 * запросом с `exact_total`.
 */
export interface TransactionsPage {
  items: Transaction[]
  total: number
  total_capped: boolean
  limit: number
  offset: number
  has_more: boolean
}

/**
 * Семь колонок со стрелками в шапке (§4) — тот же белый список, что у ручки
 * (`SORT_COLUMNS`). Отсортировать по чему-то ещё нельзя: ручка ответит 422, и
 * стрелка на такой колонке была бы обманом.
 */
export const TRANSACTION_SORTS = [
  "transaction_id",
  "fraud_score",
  "state",
  "user_id",
  "transaction_amount_eur",
  "date",
  "transaction_status",
] as const
export type TransactionSort = (typeof TRANSACTION_SORTS)[number]

/** Значение переключателя периода. `custom` берёт границы из `from`/`to`. */
export type TransactionPeriod = Period | "custom"

export interface TransactionsQuery {
  q?: string
  state?: string
  period?: TransactionPeriod
  /** Границы своего интервала — только при `period=custom`, как у ручки. */
  from?: string
  to?: string
  sort?: TransactionSort
  order?: SortOrder
  limit?: number
  offset?: number
  /** Точное число строк вместо потолка — только по явной просьбе: на 12M это
   *  полный подсчёт окна. */
  exact_total?: boolean
}

/**
 * Страница списка транзакций.
 *
 * Постранично — `limit`/`offset`, а не `page`/`page_size`: так у этой ручки,
 * и переводить одно в другое на клиенте значило бы завести второй словарь имён.
 * Сотня — потолок на стороне ручки, больше она не отдаст.
 */
export function listTransactions(params: TransactionsQuery = {}, signal?: AbortSignal) {
  const resolved = {
    q: params.q,
    state: params.state,
    period: params.period,
    from: params.from,
    to: params.to,
    sort: params.sort ?? "date",
    order: params.order ?? "desc",
    limit: params.limit ?? PAGE_SIZE,
    offset: params.offset ?? 0,
    // Только `true`: без просьбы адрес запроса остаётся прежним.
    exact_total: params.exact_total || undefined,
  }
  return request<TransactionsPage>(`/api/transactions${query(resolved)}`, { signal })
}

// ── карточка транзакции ──────────────────────────────────────────────────────

/**
 * Правило из `applied_rule_ids`, склеенное со `scoring_rules` аккаунта, — ровно
 * `AppliedRule` из `services/admin_api/transactions.py`.
 *
 * `name` пуст — правила с таким `seon_id` в каталоге аккаунта нет. Строка всё
 * равно рисуется: молча выбросить сработавшее правило значит соврать аналитику
 * о том, из чего сложился счёт. `action_details` — вклад как записан в каталоге
 * (`+100`, `+0.01`, `-1`); печатается как есть, считать там нечего.
 */
export interface AppliedRule {
  seon_id: string
  name: string | null
  action: string | null
  action_details: string | null
  /**
   * Вклад, который правило на самом деле дало в счёт этой транзакции, если он
   * не равен номиналу каталога (`+6.42` у 10008 с модификатором SEON,
   * AMEND-112). `null` — вес не менялся или транзакция старше правки: тогда в
   * Output идёт `action_details`, как раньше.
   */
  applied_output: string | null
  /**
   * Правило семейства `B` — чёрный список («все же вот эти Б правила - это
   * блеклист», AMEND-12). Признак по номеру правила, и это всё: какое поле
   * правило проверяло, из данных не следует и на экран не выводится.
   */
  blacklist: boolean
  /**
   * Вклад правила ЧС так, как его печатает SEON (`+5`), и честная строка о том,
   * что решение принято обрывом счёта, а не этим весом (AMEND-13 §3). Непусты
   * только при `blacklist`: у строк `B*` нет записи в каталоге, а значит и
   * `action_details`. Цифра совпадает с SEON, механика рядом названа своими
   * словами — вместо того чтобы молча выдать номинал за слагаемое.
   */
  blacklist_output: string | null
  blacklist_note: string | null
  /**
   * Ссылка на карточку нашего правила ЧС в каталоге — `local/blacklist`
   * (AMEND-13 §3). Это **не** утверждение «`B92` и есть это правило»: имя
   * правила каталога на строку `B*` не переносится, соответствия «номер ↔ тип
   * поля» никто не мерил. Поле говорит только «вклад в колонке Output взят вот
   * отсюда». `null` — правила в каталоге нет, цифра приехала от константы.
   */
  blacklist_rule: string | null
}

/**
 * Состоит ли поле этой транзакции в списке — факт из **нашей** базы
 * (`ListMembership`). Три ответа, а не два: `true` — состоит, `false` — не
 * состоит, `null` — нет ответа (списка нет, записей этого типа нет, сверять
 * нечего). `null` рисуется как «нет ответа»: «нет» на этом месте было бы
 * молчаливым нулём.
 *
 * Значения поля здесь нет — только имя типа и ответ.
 */
export interface ListMembership {
  list_name: string
  field_type: string
  answer: boolean | null
  /** Заполнена только при `answer === null` — почему ответа нет. */
  reason: string | null
}

/** Счётчики блока Identity: всё по `user_id` в пределах аккаунта. */
export interface UserTotals {
  transaction_count: number
  total_amount_eur: string | number | null
  first_seen: string | null
}

/**
 * Карточка: ядро и три дочерние ветки одним ответом (`TransactionCard`).
 *
 * `core`, `email`, `phone`, `session` — словари **имён колонок выгрузки**, а не
 * типизированные модели: карточка показывает то, что приехало из SEON, и второго
 * словаря имён на фронте не заводим. Значения приходят как есть (строка, число,
 * `null`); разбирает их `cardValue`/`flag` на экране, а не этот клиент.
 *
 * Ветка дочерней таблицы `null` — строки в ней нет вовсе; это не то же самое,
 * что строка из пустых колонок, и экран рисует эти два случая по-разному.
 */
export interface TransactionCard {
  core: Record<string, unknown>
  email: Record<string, unknown> | null
  phone: Record<string, unknown> | null
  session: Record<string, unknown> | null
  applied_rules: AppliedRule[]
  /** Восемь известных типов поля, сверенных с чёрным списком нашей базы. */
  lists: ListMembership[]
  /** Разобранный JSON колонки `custom_fields`. `null` — колонка пуста или не JSON. */
  custom_fields: Record<string, unknown> | null
  totals: UserTotals
}

/** Сосед в левой колонке карточки: имя, id, счёт и цветная точка по `state`. */
export interface NeighbourRow {
  id: string
  transaction_id: string | null
  user_fullname: string | null
  fraud_score: string | number | null
  state: string | null
}

/**
 * Окно вокруг открытой транзакции в **текущей выборке**.
 *
 * `position` — её номер в выборке (с единицы), `total` — длина выборки: без них
 * прокрутка не знает, где она находится. `prev_id`/`next_id` — соседи для
 * стрелок; `null` на краю выборки.
 */
export interface Neighbours {
  items: NeighbourRow[]
  position: number
  total: number
  prev_id: string | null
  next_id: string | null
}

/**
 * Фильтры соседей — те же, что были на списке (`TransactionsQuery` без
 * пагинации) плюс `around`. Приезжают из адресной строки карточки: иначе
 * «предыдущая» в карточке означала бы не ту строку, что стояла выше в таблице.
 */
export interface NeighboursQuery {
  q?: string
  state?: string
  period?: TransactionPeriod
  from?: string
  to?: string
  sort?: TransactionSort
  order?: SortOrder
  around?: number
}

/** Ядро, почта, телефон и сессия одним ответом плюс склейка правил. */
export function getTransaction(id: string, signal?: AbortSignal) {
  return request<TransactionCard>(`/api/transactions/${encodeURIComponent(id)}`, { signal })
}

/** Все колонки транзакции одним плоским словарём — под вкладку Raw Data. */
export function getTransactionRaw(id: string, signal?: AbortSignal) {
  return request<Record<string, unknown>>(
    `/api/transactions/${encodeURIComponent(id)}/raw`,
    { signal },
  )
}

/**
 * Соседи открытой транзакции в том же срезе, что и список. Ручка отвечает 404,
 * если транзакция в этот срез не попала, — карточка держит это отдельным
 * состоянием и всё равно показывает саму карточку.
 */
export function getNeighbours(id: string, params: NeighboursQuery = {}, signal?: AbortSignal) {
  const resolved = {
    q: params.q,
    state: params.state,
    period: params.period,
    from: params.from,
    to: params.to,
    sort: params.sort ?? "date",
    order: params.order ?? "desc",
    around: params.around,
  }
  return request<Neighbours>(
    `/api/transactions/${encodeURIComponent(id)}/neighbours${query(resolved)}`,
    { signal },
  )
}

// ── реплей ───────────────────────────────────────────────────────────────────

export const REPLAY_MODES = ["full", "custom_only"] as const
export type ReplayMode = (typeof REPLAY_MODES)[number]

/**
 * Группа причины «не хватает данных» (AMEND-39 шаг 3). Порядок — порядок показа.
 * Подпись по-русски приходит в `label`; экран показывает свою, по ключу.
 */
export const MISSING_GROUPS = ["request", "device_sdk", "no_source", "source_failed"] as const
export type MissingGroup = (typeof MISSING_GROUPS)[number]

export interface MissingReason {
  field: string
  group: MissingGroup
  label: string
  /** Откуда группа: «нет в кэше», «в словаре SEON: Input / User details»… */
  note: string
  /** Источник `enricher` (`proxycheck` / `refdata` / `hibp`), если поле его. */
  source: string | null
}

export interface MissingField extends MissingReason {
  /** Сколько правил записи не досчитано из-за этого поля. */
  rules: number
}

export interface RuleOutcome {
  rule_type: string
  seon_id: string
  name: string | null
  status: string
  score: string
  rule_score: string
  reason: string | null
  missing_fields: string[]
  /** Причина по каждому имени из `missing_fields`, в том же порядке. */
  missing_reasons: MissingReason[]
  condition_version: number | null
}

export interface ProviderState {
  status: "ok" | "unknown" | "skipped" | "quota"
  detail: string | null
  elapsed_ms: number | null
}

export interface ReplayEnrichment {
  /** `true` — батч, только кэш; `false` — одна запись, как живое событие. */
  cache_only: boolean
  providers: Record<string, ProviderState>
  missing: Record<string, string>
  error: string | null
}

export interface ReferenceScore {
  status: string
  field: string | null
  score: string | null
  note: string | null
}

export interface ReplayRow {
  index: number
  /** Итог. `null` — собрать его нечем; ноль на этом месте был бы посчитанным результатом. */
  score: string | null
  computed_score: string
  decision: string | null
  complete: boolean
  counts: Record<string, number>
  checked_rules: number
  skipped_rules: number
  reference: ReferenceScore
  rules_included: string
  rules: RuleOutcome[]
  unknown_fields: string[]
  warnings: string[]
  enrichment: ReplayEnrichment
  /** Заполненное поле -> источник: `record`, `proxycheck`, `refdata`, `hibp`. */
  field_sources: Record<string, string>
  /** Пустые поля, из-за которых правила не досчитаны, — с причиной. */
  missing_data: MissingField[]
}

export interface Thresholds {
  approve_below: string
  decline_at: string
  source: string
  is_assumption: boolean
  note: string | null
  decisions: string[]
}

export interface ReplayResult {
  mode: string
  rows_in: number
  rules_total: number
  excluded_rules: number
  conditions_version: string | null
  thresholds: Thresholds
  rows: ReplayRow[]
}

export interface ReplayQuery {
  mode?: ReplayMode
  reference_score_field?: string
}

/**
 * Прогон одной записи через движок.
 *
 * Запись уходит ровно такой, какой её ввёл аналитик. Ни дефолтных полей, ни
 * «поправим на лету» здесь нет: подставленное фронтом значение означало бы, что
 * экран показывает результат по записи, которой не было.
 */
export function replayRecord(
  record: Record<string, unknown>,
  params: ReplayQuery = {},
  signal?: AbortSignal,
) {
  const resolved = {
    mode: params.mode ?? "full",
    reference_score_field: params.reference_score_field,
    rules: "all",
  }
  return request<ReplayResult>(`/api/scoring/replay${query(resolved)}`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(record),
    signal,
  })
}

// ── вход ─────────────────────────────────────────────────────────────────────

/** Кто вошёл. Ровно `IdentityOut` из `services/admin_api/auth.py`. */
export interface Identity {
  account_id: number
  account_name: string
  user_id: number
  email: string
  role: string
}

/** Админ аккаунта. Строка та же, что в `backend.models.accounts.ADMIN_ROLE`. */
export const ADMIN_ROLE = "admin"

/** Привязка приложения второго фактора — показывается ровно один раз. */
export interface Enrollment {
  otpauth_uri: string
  qr_svg: string
}

/**
 * Ответ первого хода. Личности здесь нет намеренно (см. `LoginOut` на бэкенде):
 * пароль принят, но человек ещё не подтвердил, что это он.
 */
export interface LoginResult {
  second_factor: string
  enrollment: Enrollment | null
}

export interface VerifyResult {
  identity: Identity
  /** Приходят один раз — в тот вход, которым подтверждена привязка. */
  recovery_codes: string[] | null
}

/**
 * Первый ход: почта и пароль. Сессии не открывает — ставит куку привязки.
 *
 * 401 здесь ожидаем (не та почта или не тот пароль) и потому не считается
 * потерей сессии: иначе неверный пароль дёргал бы редирект на вход.
 */
export function login(email: string, password: string) {
  return request<LoginResult>(
    "/api/auth/login",
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ email, password }) },
    { expected401: true },
  )
}

/**
 * Второй ход: код с телефона или код восстановления.
 *
 * Промежуточного токена в теле нет — он приехал кукой `af_login`, которую
 * поставил `login`. Поэтому у этого запроса нет ни одного аргумента, кроме
 * самого кода: подменить «за кого входим» здесь нечем.
 */
export function verifySecondFactor(code: string) {
  return request<VerifyResult>(
    "/api/auth/verify",
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ code }) },
    { expected401: true },
  )
}

export interface LogoutResult {
  revoked: boolean
}

/** Гасит сессию в базе и стирает куку. Открыт и без живой сессии. */
export function logout() {
  return request<LogoutResult>("/api/auth/logout", { method: "POST" })
}

/**
 * Кто вошёл. 401 — «никто», и это обычный ответ на первой загрузке страницы,
 * а не потеря сессии: крюк `onSessionLost` здесь молчит.
 */
export function getIdentity(signal?: AbortSignal) {
  return request<Identity>("/api/auth/me", { signal }, { expected401: true })
}

// ── свой аккаунт ─────────────────────────────────────────────────────────────

/** Аккаунт вошедшего. Ровно `AccountOut` из `services/admin_api/account.py`. */
export interface Account {
  id: number
  name: string
}

/**
 * Потолок имени аккаунта — то же число, что у ручки и у скрипта заведения
 * (`NAME_MAX_LENGTH`). Продублировано, потому что общего словаря констант между
 * питоном и фронтом в проекте нет; разъедутся — увидит тест
 * `test_frontend_limit_matches_handler`.
 */
export const ACCOUNT_NAME_MAX_LENGTH = 120

/** Имя и номер своего аккаунта. Только для админа: остальным ручка ответит 403. */
export function getAccount(signal?: AbortSignal) {
  return request<Account>("/api/account", { signal })
}

/**
 * Переименовать **свой** аккаунт. Номера в запросе нет ни в пути, ни в теле:
 * арендатор берётся из сессии, и другого аккаунта отсюда не достать.
 *
 * Возвращается то, что легло в базу (края обрезаны на сервере), — его и надо
 * показывать, а не введённую строку.
 */
export function renameAccount(name: string) {
  return request<Account>("/api/account", {
    method: "PATCH",
    headers: JSON_HEADERS,
    body: JSON.stringify({ name }),
  })
}

// ── люди аккаунта ────────────────────────────────────────────────────────────

/** Человек в списке. Пароля и хеша в этом типе нет — их нет и в ответе. */
export interface Person {
  id: number
  email: string
  role: string
  is_active: boolean
  created_at: string
  /** `null` — доступ выдан, но человек им ни разу не воспользовался. */
  last_login_at: string | null
  totp_enabled: boolean
}

export interface PeopleList {
  items: Person[]
  account_id: number
  account_name: string
}

/**
 * Заведённый человек и его пароль — в первый и последний раз.
 *
 * Значение живёт только в памяти вкладки: в состояние вне компонента, в
 * `localStorage` и в адресную строку оно не уходит ни на шаг (AMEND-8 §5).
 */
export interface CreatedPerson {
  person: Person
  password: string
}

/** Список людей своего аккаунта. Чужих не отдаст политика базы, не фронт. */
export function listPeople(signal?: AbortSignal) {
  return request<PeopleList>("/api/people", { signal })
}

export function createPerson(email: string, role: string) {
  return request<CreatedPerson>("/api/people", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ email, role }),
  })
}

/**
 * Доступ и роль. `undefined` — «не трогать»: ручка отличает это от `false`.
 */
export function updatePerson(
  id: number,
  patch: { is_active?: boolean; role?: string },
) {
  return request<Person>(`/api/people/${id}`, {
    method: "PATCH",
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  })
}

// ── журнал действий (AMEND-35 шаг 2) ─────────────────────────────────────────

/** Страница журнала — та же полусотня, что `DEFAULT_LIMIT` ручки. */
export const AUDIT_PAGE_SIZE = 50

/**
 * Как рисовать «что изменилось». Решает ручка по действию, а не экран по
 * форме пары: у правки комментария «было» тоже бывает `null`, и по одной паре
 * создание от правки из пустого не отличить.
 */
export type AuditShape = "created" | "removed" | "changed"

/** Одно поле: подпись по-русски и пара «было → стало» как она лежит в базе. */
export interface AuditChange {
  field: string
  label: string
  before: unknown
  after: unknown
}

/** Строка журнала. Ровно `AuditRow` из `services/admin_api/audit.py`. */
export interface AuditRow {
  id: number
  created_at: string
  user_id: number | null
  actor_email: string | null
  ip: string | null
  action: string
  action_label: string
  object_type: string | null
  object_type_label: string | null
  object_id: string | null
  object_label: string | null
  shape: AuditShape
  changes: AuditChange[]
}

export interface AuditPage {
  items: AuditRow[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface AuditFilters {
  actions: { action: string; label: string }[]
  people: { user_id: number; email: string | null }[]
}

export interface AuditQuery {
  /** Границы периода — ISO-моменты; обе включительно. */
  from?: string
  to?: string
  user_id?: number
  action?: string
  offset?: number
}

/** Журнал своего аккаунта: аккаунт ручка берёт из сессии, параметра у неё нет. */
export function listAudit(params: AuditQuery = {}, signal?: AbortSignal) {
  const resolved = {
    from: params.from,
    to: params.to,
    user_id: params.user_id,
    action: params.action,
    limit: AUDIT_PAGE_SIZE,
    offset: params.offset ?? 0,
  }
  return request<AuditPage>(`/api/audit${query(resolved)}`, { signal })
}

export function getAuditFilters(signal?: AbortSignal) {
  return request<AuditFilters>("/api/audit/filters", { signal })
}

// ── таймауты решения (AMEND-97 шаг 2) ────────────────────────────────────────

/**
 * Когда правка доходит до процесса: `live` — читается в запросе через кеш с
 * TTL (`cache_ttl_seconds`), `restart` — только при старте сервиса.
 */
export type TimeoutApplies = "live" | "restart"

/** Один таймаут или бюджет. Ровно элемент ответа `GET /api/settings/timeouts`. */
export interface TimeoutSetting {
  key: string
  label: string
  description: string
  /** Сервис, который читает значение (`enricher`, `scoring`…). */
  service: string
  /** Действующее значение: правка из базы, а без неё — умолчание из env. */
  value: number
  /** Умолчание из env — то, к чему ведёт «сбросить». */
  default: number
  min: number
  /** Верхний предел включительно. */
  max: number
  /** `true` — значение строго больше `min`, иначе `min` допустим. */
  min_exclusive: boolean
  unit: "s"
  applies: TimeoutApplies
  /** В базе лежит своя правка (а не умолчание из env). */
  overridden: boolean
  updated_at: string | null
  updated_by: string | null
}

export interface TimeoutSettings {
  items: TimeoutSetting[]
  /** Через сколько секунд `live`-правку увидят все процессы. */
  cache_ttl_seconds: number
}

/** Таймауты. Только для админа: остальным ручка ответит 403. */
export function getTimeouts(signal?: AbortSignal) {
  return request<TimeoutSettings>("/api/settings/timeouts", { signal })
}

/**
 * Правка таймаутов. В `values` — только изменённые ключи; `null` — сбросить к
 * умолчанию из env. Ответ — весь набор заново, в том виде, как он лёг в базу:
 * его и надо показывать. 422 несёт в `detail` объяснение по-русски (например,
 * таймаут enricher не выше бюджета обогащения).
 */
export function updateTimeouts(values: Record<string, number | null>) {
  return request<TimeoutSettings>("/api/settings/timeouts", {
    method: "PUT",
    headers: JSON_HEADERS,
    body: JSON.stringify({ values }),
  })
}

// ── записи списков (AMEND-12 §4) ─────────────────────────────────────────────

/**
 * Страница списка — 25 строк, как на стороне ручки (`DEFAULT_LIMIT`).
 *
 * Не `PAGE_SIZE`: в ЧС стенда 30 000 записей, и сотня строк со значениями ПДн
 * на экране — это сотня строк, которые попадут в случайный скриншот. Вкладкам
 * скоринга сотня нужна, здесь — нет.
 */
export const LIST_PAGE_SIZE = 25

export interface ListEntry {
  id: number
  value: string
  field_type: string
  comment: string | null
  expires_at: string | null
  updated_by: string | null
  updated_at: string
  /**
   * Срок вышел. Считает **сервер**: у браузера свои часы, и на отставшем
   * компьютере просроченная запись выглядела бы живой.
   */
  expired: boolean
}

export interface ListEntriesPage {
  items: ListEntry[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface FieldTypeCount {
  field_type: string
  count: number
}

export interface FieldTypes {
  /** Типы, которые в списке **есть**, с числом записей — для фильтра. */
  present: FieldTypeCount[]
  /** Восемь известных имён — подсказки формы. Приходят с сервера, не из копии. */
  known: string[]
}

export interface ListEntriesQuery {
  q?: string
  field_type?: string
  order?: SortOrder
  limit?: number
  offset?: number
}

export function listEntries(
  list: string,
  params: ListEntriesQuery = {},
  signal?: AbortSignal,
) {
  const resolved = {
    q: params.q,
    field_type: params.field_type,
    // Ключ сортировки один — дата правки (§4). Проставлен явно по той же
    // причине, что у правил: молчаливый серверный дефолт дал бы два разных
    // адреса на один и тот же экран.
    sort: "updated_at",
    order: params.order ?? "desc",
    limit: params.limit ?? LIST_PAGE_SIZE,
    offset: params.offset ?? 0,
  }
  return request<ListEntriesPage>(
    `/api/lists/${encodeURIComponent(list)}/entries${query(resolved)}`,
    { signal },
  )
}

export function listFieldTypes(list: string, signal?: AbortSignal) {
  return request<FieldTypes>(`/api/lists/${encodeURIComponent(list)}/field-types`, {
    signal,
  })
}

export interface NewListEntry {
  field_type: string
  value: string
  comment?: string | null
  /** Срок необязателен: без него запись бессрочная. */
  expires_at?: string | null
}

export function createListEntry(list: string, entry: NewListEntry) {
  return request<ListEntry>(`/api/lists/${encodeURIComponent(list)}/entries`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(entry),
  })
}

/**
 * Комментарий и срок. Ключ, которого нет в объекте, ручка не трогает; `null`
 * — снимает. Поэтому здесь именно `Partial`, а не поля со значением по
 * умолчанию: `undefined` и «пусто» тут значат разное.
 */
export function updateListEntry(
  list: string,
  id: number,
  patch: { comment?: string | null; expires_at?: string | null },
) {
  return request<ListEntry>(`/api/lists/${encodeURIComponent(list)}/entries/${id}`, {
    method: "PATCH",
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  })
}

/** Удаление. Ответ — 204 без тела, поэтому не `request`: тому нужен JSON. */
export function deleteListEntry(list: string, id: number) {
  return requestEmpty(`/api/lists/${encodeURIComponent(list)}/entries/${id}`, {
    method: "DELETE",
  })
}

/* ────────────────────────────  Заливка выгрузки  ──────────────────────────── */

/**
 * Ручки фоновой заливки (AMEND-16 §3 шаг 3A, `services/admin_api/uploads.py`).
 *
 * Своих ручек экран не заводит и ни одного числа не досчитывает: и ход, и итог
 * приходят с сервера готовыми. Это не стилистика, а условие того, чтобы вкладку
 * можно было закрыть: состояние, собранное в памяти страницы, умирает вместе с
 * ней, а спросить его потом будет уже не у кого.
 */

/**
 * Состояния задачи (`backend/upload_runner.py`).
 *
 * `cancelled` сюда входит, хотя ручки отмены нет: статус существует в базе, и
 * молчаливый `default` в разборе показал бы такую задачу как «неизвестно что».
 */
export type UploadState = "queued" | "running" | "done" | "failed" | "cancelled"

/** Сколько файла разобрано. **Про файл, а не про строки в таблице.** */
export interface UploadProgress {
  bytes_done: number
  bytes_total: number
  percent: number
}

/** Агрегаты разбора: `read = inserted + duplicates + bad_rows` в любой момент. */
export interface UploadReport {
  read: number
  inserted: number
  duplicates: number
  bad_rows: number
  rows_covered: number
  covers_from: string | null
  covers_to: string | null
  unknown_columns: string[]
  missing_columns: string[]
}

export interface Upload {
  id: number
  status: UploadState
  source_file: string
  size_bytes: number
  progress: UploadProgress
  report: UploadReport
  /** Запись реестра покрытия. Есть только у завершённой успехом. */
  coverage_id: number | null
  /** Тип исключения у отказавшей. Без текста: текст вкладывает в себя ячейку. */
  failure: string | null
  /** Сколько раз задачу брали в работу. Больше одного — была докачка. */
  attempts: number
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export function listUploads() {
  return request<Upload[]>("/api/uploads")
}

export function getUpload(id: number) {
  return request<Upload>(`/api/uploads/${id}`)
}

/* ──────────────────  Сколько истории набралось (индикатор)  ────────────────── */

/** Отрезок суток, границы включительно: кусок покрытия или дыра между кусками. */
export interface Stretch {
  day_from: string
  day_to: string
  days: number
}

/**
 * Факты индикатора (AMEND-16 §3 шаг 4, `services/admin_api/coverage.py`).
 *
 * Ручка отдаёт **факты, а не фразу**: строку из них собирает экран. Арифметика
 * при этом на сервере и проверена `pytest`, поэтому здесь ничего не считают
 * заново — `depth_days + missing_days + stale_days` всегда равны `target_days`,
 * и вся строка стоит на этом.
 */
export interface HistoryCoverage {
  /** Края всего покрытия. `null` — не залито ничего. */
  covered_from: string | null
  covered_to: string | null
  /** Легли ли куски подряд, и набран ли год целиком. */
  contiguous: boolean
  complete: boolean
  /** Куски и дыры — чтобы назвать дыру датами, а не числом. */
  pieces: Stretch[]
  gaps: Stretch[]
  target_days: number
  /** Начало непрерывной глубины и её длина. Суммой кусков это не бывает. */
  depth_from: string | null
  depth_days: number
  /** Не хватает **раньше начала**: окна правил смотрят назад от скоримой строки. */
  missing_days: number
  missing_from: string | null
  missing_to: string | null
  /** Не долито у свежего края. Растёт само, простоем. */
  stale_days: number
  as_of: string
}

export function getCoverage() {
  return request<HistoryCoverage>("/api/coverage")
}

/**
 * Передачу прервали из страницы — это не отказ ручки.
 *
 * Отдельным классом, а не кодом 0 у `ApiError`: отменивший сам человек не
 * должен увидеть красную полосу «не удалось», а различить эти два случая по
 * тексту сообщения значило бы сверять строки.
 */
export class UploadAborted extends Error {
  constructor() {
    super("upload aborted")
    this.name = "UploadAborted"
  }
}

export interface UploadHandle {
  /** Обрывает **передачу** файла. Разбор этим не трогается: его ещё нет. */
  abort: () => void
  done: Promise<Upload>
}

/**
 * Отправляет файл и отдаёт номер заведённой задачи (202).
 *
 * На `XMLHttpRequest`, а не на `fetch`, ровно по одной причине: `fetch` не
 * умеет сообщать, сколько **отправлено**. Пока сотни мегабайт едут на сервер,
 * ручка молчит — и на `fetch` экран стоял бы неподвижно несколько минут, то
 * есть выглядел бы зависшим именно в тот момент, когда всё идёт хорошо.
 *
 * Заголовок `Content-Type` не ставится намеренно: границу `multipart` знает
 * только браузер, и заданный руками заголовок её затирает — ручка получает
 * тело, которое не может разобрать, и отвечает 422 на совершенно правильный
 * файл. Кука сессии едет сама: адрес тот же, `withCredentials` для
 * `same-origin` не нужен.
 */
export function startUpload(
  file: File,
  onProgress?: (sent: number, total: number) => void,
): UploadHandle {
  const body = new FormData()
  body.append("file", file, file.name)

  const xhr = new XMLHttpRequest()
  const done = new Promise<Upload>((resolve, reject) => {
    xhr.open("POST", `${BASE}/api/uploads`)
    xhr.setRequestHeader("Accept", "application/json")

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total)
    })

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as Upload)
        } catch {
          reject(new ApiError(xhr.status, "the response was not JSON"))
        }
        return
      }
      // Разбор отказа повторяет `request` дословно, включая увод на вход по
      // 401: сессия кончается и посреди заливки, и тогда экран обязан вести
      // себя так же, как любой другой.
      let detail = `HTTP ${xhr.status}`
      try {
        const parsed = JSON.parse(xhr.responseText) as { detail?: unknown }
        if (typeof parsed.detail === "string") detail = parsed.detail
      } catch {
        /* тело не JSON — остаётся код */
      }
      // 413 приходит не от ручки, а от nginx, и телом у него HTML: разбор выше
      // ничего не находит, и владелица увидела бы голое «HTTP 413». Предел
      // стоит в `frontend/nginx.conf` (`location /api/uploads`), и сказать про
      // него надо здесь — на экране про nginx не знают.
      if (xhr.status === 413) detail = "the file is larger than the server accepts"
      if (xhr.status === 401) sessionLost?.()
      reject(new ApiError(xhr.status, detail))
    })

    xhr.addEventListener("error", () => reject(new ApiError(0, "the request did not reach the server")))
    xhr.addEventListener("abort", () => reject(new UploadAborted()))

    xhr.send(body)
  })

  return { abort: () => xhr.abort(), done }
}

/* ------------------------------------------------------------------------- */
/* Живые пары «мы и SEON» (AMEND-59 шаг 2)                                    */
/* ------------------------------------------------------------------------- */

/**
 * Поля и порядок — как в моделях `services/admin_api/seon_live.py`; тест
 * `tests/test_seon_live.py` держит их равными. Балл и Δ приходят из `numeric`
 * строкой — как везде в консоли.
 */
export type LivePeriod = "today" | "7d" | "custom"

export interface LiveOurSide {
  id: string
  fraud_score: string | number | null
  state: string | null
  date: string | null
}

/** Откуда решение SEON: ручная проверка, поле `state` ответа, порог балла. */
export type LiveDecisionSource = "review" | "state" | "score"

export interface LiveSeonSide {
  seon_id: string | null
  fraud_score: string | number | null
  state: string | null
  version: number
  received_at: string
  signature_verified: boolean
  /** Решение SEON; `null` — ни проверки, ни `state`, ни балла. */
  decision: string | null
  decision_source: LiveDecisionSource | null
  /** Итог склейки приёмника: `order_memo`, `attributes`, `ambiguous`, `none`… */
  twin_match: string | null
}

export interface LiveRuleRef {
  seon_id: string
  name: string | null
  blacklist: boolean
}

export interface LiveRuleDiff {
  comparable: boolean
  matched: LiveRuleRef[]
  extra: LiveRuleRef[]
  missed: LiveRuleRef[]
}

export interface LiveSeonReview {
  state: string | null
  label: string | null
  label_removed: boolean
  updated_at: string | null
  events: number
}

export interface LivePair {
  key: string
  at: string
  transaction_id: string | null
  ours: LiveOurSide | null
  seon: LiveSeonSide | null
  missing: "seon" | "ours" | null
  /** Чем склеена полная пара: `transaction_id`, `order_memo`, `attributes`. */
  match: string | null
  delta: string | number | null
  decision_mismatch: boolean
  rules_differ: number | null
  rules: LiveRuleDiff | null
  review: LiveSeonReview | null
}

export interface LivePairsPage {
  items: LivePair[]
  total: number
  limit: number
  offset: number
  has_more: boolean
  since: string
  until: string
}

export interface LiveMatrix {
  rows: number
  agreed: number
  share_agreed: string | null
  /** `seon_<решение>` → `our_<решение>` → число пар. */
  cells: Record<string, Record<string, number>>
  undecided: number
}

export interface LiveDeltaStats {
  rows: number
  mean: string | number | null
  mean_abs: string | number | null
  median_abs: string | number | null
  p90_abs: string | number | null
}

export interface LiveRuleCount {
  seon_id: string
  name: string | null
  blacklist: boolean
  count: number
}

export interface LiveThresholds {
  approve_below: string | number
  decline_at: string | number
}

export interface LiveSummary {
  period: LivePeriod
  since: string
  until: string
  ours: number
  seon: number
  pairs: number
  ours_only: number
  seon_only: number
  /** Из `seon_only`: две равноудалённые строки двойника, пары нет. */
  ambiguous: number
  pairs_by_match: Record<string, number>
  seon_decision_sources: Record<string, number>
  thresholds: LiveThresholds
  matrix: LiveMatrix
  delta: LiveDeltaStats
  rules_compared: number
  top_extra: LiveRuleCount[]
  top_missed: LiveRuleCount[]
}

export interface SeonPipe {
  day: string
  last_webhook_at: string | null
  last_accepted_at: string | null
  accepted: number
  rejected: number
  unsigned: number
  duplicates: number
  broken: number
  twin_last_24h: number
  silent: boolean
}

export interface LivePeriodQuery {
  period: LivePeriod
  /** Даты `YYYY-MM-DD` включительно, только для `custom`. */
  from?: string
  to?: string
}

export interface LivePairsQuery extends LivePeriodQuery {
  mismatch?: boolean
  min_delta?: string
  unpaired?: boolean
  limit?: number
  offset?: number
}

function livePeriodParams(params: LivePeriodQuery) {
  // Даты уходят только со своим периодом: ручка на `today` их не ждёт, и
  // забытые в поле значения не должны выглядеть так, будто они что-то режут.
  return params.period === "custom"
    ? { period: params.period, from: params.from, to: params.to }
    : { period: params.period }
}

export function getSeonLiveSummary(params: LivePeriodQuery, signal?: AbortSignal) {
  return request<LiveSummary>(`/api/seon-live/summary${query(livePeriodParams(params))}`, {
    signal,
  })
}

export function getSeonLivePairs(params: LivePairsQuery, signal?: AbortSignal) {
  return request<LivePairsPage>(
    `/api/seon-live/pairs${query({
      ...livePeriodParams(params),
      // `false` не шлём: ручка понимает отсутствие флага так же, а адрес короче.
      mismatch: params.mismatch || undefined,
      min_delta: params.min_delta,
      unpaired: params.unpaired || undefined,
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  )
}

export function getSeonLivePipe(signal?: AbortSignal) {
  return request<SeonPipe>("/api/seon-live/pipe", { signal })
}

/** Пара для карточки двойника: `seon = null` — вебхука по ней ещё нет. */
export function getSeonLivePair(twinId: string, signal?: AbortSignal) {
  return request<LivePair>(`/api/seon-live/pairs/${encodeURIComponent(twinId)}`, { signal })
}
